import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Credit: https://ui.noxhd.com/components/image-zoom/

gsap.registerPlugin(ScrollTrigger);

const CONTENT_ANIM_START = 0.15;
const CONTENT_ANIM_DURATION = 0.25;
const INTERACTIVE_THRESHOLD = CONTENT_ANIM_START + CONTENT_ANIM_DURATION;
const OPACITY_FADE_END = 0.15;
const DOOR_PHASE_END = 0.5;
const INSIDE_SEQUENCE_START = 0.34;
const INSIDE_SEQUENCE_END = 1;
const INSIDE_CROSSFADE_SECONDS = 0.8;

const insideSequenceControllers = new WeakMap<HTMLElement, { destroy: () => void }>();

const DOOR_ZOOM = {
  scale: 40,
  y: "405vh",
  x: "-17%",
  ease: "power1.inOut",
};

const startLoopingVideo = (video: HTMLVideoElement) => {
  video.muted = true;
  video.loop = true;
  video.playsInline = true;

  const play = () => {
    void video.play().catch(() => { });
  };

  play();
  video.addEventListener("ended", play);
};

const setInteractive = (
  content: HTMLElement | null,
  overlay: HTMLElement | null,
  isInteractive: boolean
) => {
  if (content) {
    content.classList.toggle("is-interactive", isInteractive);
  }

  if (overlay) {
    overlay.classList.toggle("is-hidden", isInteractive);
  }
};

type InsideImageSequence = {
  update: (progress: number) => void;
  destroy: () => void;
};

const getTargetSlideIndex = (
  progress: number,
  slideCount: number,
  sequenceStart: number,
  sequenceEnd: number
) => {
  if (slideCount <= 1 || progress < sequenceStart) {
    return 0;
  }

  const segmentDuration = (sequenceEnd - sequenceStart) / slideCount;

  for (let index = slideCount - 1; index >= 1; index -= 1) {
    if (progress >= sequenceStart + index * segmentDuration) {
      return index;
    }
  }

  return 0;
};

const createInsideImageSequence = (
  slides: HTMLElement[],
  sequenceStart: number,
  sequenceEnd: number
): InsideImageSequence => {
  if (slides.length <= 1) {
    return {
      update: () => { },
      destroy: () => { },
    };
  }

  gsap.set(slides, { opacity: 0 });
  gsap.set(slides[0], { opacity: 1 });

  let currentIndex = 0;
  let isTransitioning = false;
  let transition: gsap.core.Timeline | null = null;

  const finishTransition = (targetIndex: number) => {
    gsap.set(slides, { opacity: 0 });
    gsap.set(slides[targetIndex], { opacity: 1 });
    currentIndex = targetIndex;
    isTransitioning = false;
    transition = null;
  };

  const transitionTo = (targetIndex: number, progress: number) => {
    if (targetIndex === currentIndex) {
      return;
    }

    transition?.kill();

    const from = slides[currentIndex];
    const to = slides[targetIndex];

    isTransitioning = true;
    gsap.set(to, { opacity: 0 });

    transition = gsap
      .timeline({
        onComplete: () => {
          finishTransition(targetIndex);
          update(progress);
        },
      })
      .to(from, { opacity: 0, duration: INSIDE_CROSSFADE_SECONDS, ease: "power2.inOut" }, 0)
      .to(to, { opacity: 1, duration: INSIDE_CROSSFADE_SECONDS, ease: "power2.inOut" }, 0);
  };

  const update = (progress: number) => {
    const targetIndex = getTargetSlideIndex(
      progress,
      slides.length,
      sequenceStart,
      sequenceEnd
    );

    if (isTransitioning || targetIndex === currentIndex) {
      return;
    }

    transitionTo(targetIndex, progress);
  };

  const destroy = () => {
    transition?.kill();
    transition = null;
    isTransitioning = false;
  };

  return { update, destroy };
};

const initImageZoom = (container: HTMLElement) => {
  const stage = container.querySelector<HTMLElement>(".image-zoom__stage");
  const media = container.querySelector<HTMLElement>(".image-zoom__media");
  const outsideVideo = container.querySelector<HTMLVideoElement>(".image-zoom__outside");
  const opacityImage = container.querySelector<HTMLElement>(".image-zoom__opacity");
  const insideSlides = container.querySelectorAll<HTMLElement>(".image-zoom__inside-slide");
  const insideVideo = container.querySelector<HTMLVideoElement>(".image-zoom__inside");
  const overlay = container.querySelector<HTMLElement>(".image-zoom__overlay");
  const contentEl = container.querySelector<HTMLElement>(".image-zoom__content");
  const hasContent = Boolean(
    contentEl && (contentEl.children.length > 0 || contentEl.textContent?.trim())
  );
  const content = hasContent ? contentEl : null;
  const hasInsideMedia = insideSlides.length > 0 || Boolean(insideVideo);

  if (!stage || !outsideVideo || !media || !hasInsideMedia) {
    return;
  }

  startLoopingVideo(outsideVideo);

  if (insideVideo) {
    startLoopingVideo(insideVideo);
    gsap.set(insideVideo, { transformOrigin: "top center", force3D: true });
  }

  if (insideSlides.length) {
    gsap.set(insideSlides, { opacity: 0 });
    gsap.set(insideSlides[0], { opacity: 1 });
  }

  gsap.set(media, { transformOrigin: "top center", force3D: true });
  gsap.set(outsideVideo, { transformOrigin: "50% 80%", force3D: true });

  if (opacityImage) {
    gsap.set(opacityImage, { transformOrigin: "50% 80%", force3D: true, opacity: 1 });
  }

  if (overlay) {
    gsap.set(overlay, { opacity: 1 });
  }

  if (content) {
    gsap.set(content, { opacity: 0, y: 24 });
    setInteractive(content, overlay, false);
  }

  const insideSequence =
    insideSlides.length > 0
      ? createInsideImageSequence(
        Array.from(insideSlides),
        INSIDE_SEQUENCE_START,
        INSIDE_SEQUENCE_END
      )
      : null;

  if (insideSequence) {
    insideSequenceControllers.set(container, insideSequence);
  }

  const timeline = gsap.timeline({
    scrollTrigger: {
      trigger: container,
      pin: stage,
      start: "top top",
      end: "+=200%",
      scrub: 2,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      onUpdate: (self) => {
        insideSequence?.update(self.progress);

        if (content) {
          setInteractive(content, overlay, self.progress >= INTERACTIVE_THRESHOLD);
        }
      },
      onLeave: () => {
        if (content) {
          setInteractive(content, overlay, true);
        }
      },
      onEnterBack: () => {
        if (content) {
          setInteractive(content, overlay, false);
        }
      },
    },
  });

  timeline.to(outsideVideo, DOOR_ZOOM, 0);

  if (insideVideo) {
    timeline.to(
      insideVideo,
      {
        scale: 1.1,
        ease: "none",
      },
      0
    );
  } else {
    timeline.to(
      media,
      {
        scale: 1.1,
        ease: "none",
      },
      0
    );
  }

  if (opacityImage) {
    timeline
      .to(opacityImage, DOOR_ZOOM, 0)
      .to(
        opacityImage,
        {
          opacity: 0,
          duration: OPACITY_FADE_END,
          ease: "none",
        },
        0
      );
  }

  if (overlay) {
    timeline.to(
      overlay,
      {
        opacity: 0,
        ease: "none",
      },
      DOOR_PHASE_END
    );
  }

  if (content) {
    timeline.to(
      content,
      {
        opacity: 1,
        y: 0,
        duration: CONTENT_ANIM_DURATION,
        ease: "none",
      },
      CONTENT_ANIM_START
    );
  }

  ScrollTrigger.refresh();
};

export const setupImageZoom = () => {
  document.querySelectorAll<HTMLElement>("[data-image-zoom]").forEach((container) => {
    initImageZoom(container);
  });
};

export const destroyImageZoom = () => {
  document.querySelectorAll<HTMLElement>("[data-image-zoom]").forEach((container) => {
    const triggers = ScrollTrigger.getAll().filter(
      (trigger) => trigger.trigger === container
    );

    triggers.forEach((trigger) => trigger.kill());
    insideSequenceControllers.get(container)?.destroy();
    insideSequenceControllers.delete(container);
    container.querySelector<HTMLVideoElement>(".image-zoom__inside")?.pause();
    container.querySelector<HTMLVideoElement>(".image-zoom__outside")?.pause();
    delete container.dataset.imageZoomInit;
  });
};
